import React, { memo, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';

import { Text, Icon, Avatar, EmptyAvatar } from '../Primitives';

import { IActivity } from '../../interface/Activity';

import { fromServer } from '../../helpers/datetime';
import {
  formatActivityPrice,
  hasDisplayablePrice,
  isPriceTba,
} from '../../helpers/activityPricing';
import { getRating } from '../../helpers/user';
import {
  getAcceptedOrReservedPlayers,
  getGenderSlotDescriptors,
  isGenderManaged,
} from '../../helpers/activityGenderSlots';
import { getGenderColor, getGenderTint } from '../../helpers/genderDisplay';
import {
  ACTIVITY_TAG_COLORS,
  getActivityPurposeTag,
  getActivityPurposeTagStyle,
  getActivityTagOutlineStyle,
} from '../../helpers/activityDisplay';

import { userStore } from '../../stores/UserStore';

import { AvatarWrapper, TagWrapper } from './styled';
import ActivityStatusPill from './ActivityStatusPill';

type Props = {
  activity: IActivity;
  hideTag?: boolean;
  isWhiteText?: boolean;
};

const MIN = 1;
const MAX = 7;

const ActivityData: React.FC<Props> = observer(
  ({ activity, hideTag, isWhiteText }) => {
    const {
      account: { profileId },
    } = userStore;

    const {
      sportInfo,
      name,
      accepted,
      discoverable,
      players,
      minPlayer,
      maxPlayer,
      minRating,
      maxRating,
      start,
      end,
      isRanked,
      timezone,
      recurring,
      locations,
      courtBooked,
      id,
      sport,
    } = activity;

    const isWorkoutParty = useMemo(() => sport === 'WORKOUT_PARTY', [sport]);

    const displayStart = fromServer(start, timezone, 'cccc MMM dd');
    const timeStart = fromServer(start, timezone, 'h:mm a');
    const timeEnd = fromServer(end, timezone, 'h:mm a');
    const activityLocation = locations[0];

    const rule = recurring ? recurring.rrule.split(':')[1].split(';') : null;

    const freq = rule ? rule[0].split('=')[1] : null;

    const goingPlayers = useMemo(() => getAcceptedOrReservedPlayers(activity), [activity]);
    const slotDescriptors = useMemo(() => {
      if (isGenderManaged(activity)) return getGenderSlotDescriptors(activity);
      return Array.from({ length: Math.max(maxPlayer - goingPlayers.length, 0) }, (_, index) => ({
        key: `open-${index}`,
        slotType: 'open' as const,
      }));
    }, [activity, goingPlayers.length, maxPlayer]);

    const renderEmptyAvatar = () => {
      return slotDescriptors.map((slot) => {
        if (slot.slotType === 'open') {
          return (
            <EmptyAvatar key={`${id}-${slot.key}-empty-player`} size="md" />
          );
        }

        const genderColor = getGenderColor(slot.slotType);

        return (
          <div
            key={`${id}-${slot.key}-empty-player`}
            className="w-12 h-12 rounded-full border border-dashed flex items-center justify-center"
            style={{
              backgroundColor: getGenderTint(slot.slotType),
              borderColor: `${genderColor}66`,
            }}
          >
            <Icon
              icon={slot.slotType}
              size="sm"
              stroke={slot.slotType === 'female' ? 'pinkEnergetic' : 'preservingBlue'}
            />
          </div>
        );
      });
    };

    const isRestrict = minRating !== MIN || maxRating !== MAX;

    const isHost =
      players.find((p) => p.role === 'ORGANISER')?.profileId === profileId;

    const purposeTag = getActivityPurposeTag(activity);
    const hostTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.HOSTING);
    const rankedTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.RANKED);
    const showPrice = hasDisplayablePrice(activity);
    const priceLabel = formatActivityPrice(activity);

    const history = useHistory();

    const textColorClass = isWhiteText ? 'text-white' : 'text-foreground';
    const secondaryTextClass = isWhiteText ? 'text-white' : 'text-muted-foreground';

    return (
      <div onClick={() => history.push(`/plays/${id}`)} className="cursor-pointer">
        <div className="flex items-center mb-2">
          <Text
            variant="lb.sm"
            className={`capitalize ${secondaryTextClass}`}
          >
            {sportInfo.name.toLowerCase()}
          </Text>
          <img
            className="ml-2 w-4"
            src={`https://rovo.fit/assets/sports/sport-${sportInfo.uri}-icon.png`}
            alt={sportInfo.name}
            loading="lazy"
            decoding="async"
          />
          <ActivityStatusPill activity={activity} className="ml-2 shrink-0" />
          <div className="ml-auto font-medium border border-primary px-2 py-1 rounded-full">
            <Text variant="lb.sm" className="text-primary">
              {isRestrict && minRating !== null && maxRating !== null
                ? `Lv ${
                    minRating === maxRating
                      ? minRating
                      : `${minRating}-${maxRating}`
                  }`
                : 'All level'}
            </Text>
          </div>
        </div>
        <Text
          variant="lb.lg"
          className={`whitespace-nowrap overflow-hidden text-ellipsis ${textColorClass}`}
        >
          {isRanked && 'Ranked game: '}
          {name}
        </Text>
        {discoverable && (
          <Text className={`mb-2 ${textColorClass}`} variant="pg.md">
            Public Activity
          </Text>
        )}
        <AvatarWrapper>
          {goingPlayers.map(
            ({ profile: { id, picture, pulseLevel, sportProfile, sportProfiles }, reserved }) => {
              // Get rating from sportProfile or find from sportProfiles by activity's sport
              const effectiveSportProfile = sportProfile || sportProfiles?.find(sp => sp.sport === sport);
              const rating = getRating(effectiveSportProfile);
              return (
                <Avatar
                  key={id}
                  size="md"
                  src={picture}
                  badgeText={rating}
                  pulseLevel={pulseLevel}
                />
              );
            }
          )}
          {renderEmptyAvatar()}
        </AvatarWrapper>
        <Text variant="pg.md" className={`mb-1 ${textColorClass}`}>
          {`${players.filter(p => accepted.includes(p.profile.id) || p.reserved).length}/${maxPlayer} Going - Min ${minPlayer} to start`}
        </Text>
        {showPrice && (
          <div className="flex items-center mb-2">
            <Text variant="lb.md" className={textColorClass}>
              {priceLabel}
              {!isPriceTba(activity) && <>&nbsp;/ person</>}
            </Text>
          </div>
        )}
        {!hideTag && (
          <TagWrapper>
            {isHost && <span className="px-2 py-0.5 rounded text-xs font-medium" style={hostTagStyle}>Host</span>}
            {isRanked && <span className="px-2 py-0.5 rounded text-xs font-medium" style={rankedTagStyle}>Ranked game</span>}
            {purposeTag && (
              <span className="px-2 py-0.5 rounded text-xs font-medium" style={getActivityPurposeTagStyle(purposeTag.purpose)}>
                {purposeTag.label}
              </span>
            )}
          </TagWrapper>
        )}
        <div className={`h-px my-3 ${isWhiteText ? 'bg-white' : 'bg-border'}`} />
        <div className="flex items-center">
          <Icon icon="time" size="md" />
          <div className="ml-2">
            <Text as="span" variant="pg.md" className={textColorClass}>
              {displayStart}, &nbsp;
            </Text>
            <Text as="span" variant="pg.md" className={textColorClass}>
              {timeStart} - {timeEnd}
            </Text>
          </div>
        </div>
        <Text
          className={`ml-7 mb-1 ${secondaryTextClass}`}
          variant="lb.md"
        >
          {!!freq ? `Repeat ${freq.toLowerCase()}` : 'One time activity'}
        </Text>
        <div className="flex items-center">
          <Icon
            icon="location"
            stroke={isWhiteText ? 'white' : 'secondaryText'}
            size="md"
          />
          {courtBooked ? (
            <>
              <Text className={`ml-2 ${textColorClass}`} variant="pg.md">
                Court Booked
              </Text>
              <span className="w-1 h-1 bg-current rounded-full mx-2" />
              <Text variant="pg.md" className={textColorClass}>
                {isWorkoutParty
                  ? 'Online'
                  : activityLocation?.venueDetail?.venueName ||
                    activityLocation?.name}
              </Text>
            </>
          ) : (
            <Text variant="pg.md" className={`ml-2 ${textColorClass}`}>
              {isWorkoutParty
                ? 'Online'
                : activityLocation?.venueDetail?.venueName ||
                  activityLocation?.name}
            </Text>
          )}
        </div>
      </div>
    );
  }
);

export default memo(ActivityData);
